import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { ApiError, api, canEdit, type AuthUser, type PhotoAlbum } from '@/lib/api';

export default function PhotoAlbums({ user }: { user: AuthUser }) {
  const editable = canEdit(user.role);
  const [creating, setCreating] = useState(false);

  const albumsQuery = useQuery({
    queryKey: ['photo-albums'],
    queryFn: api.listPhotoAlbums,
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Photo albums</h1>
          <p className="text-sm text-slate-400">
            One entry per player per album — photo + short bio.
          </p>
        </div>
        {editable && !creating && (
          <button
            className="flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
            onClick={() => setCreating(true)}
          >
            <Plus size={16} /> New album
          </button>
        )}
      </header>

      {creating && editable && (
        <CreateAlbumDialog onClose={() => setCreating(false)} />
      )}

      {albumsQuery.isLoading && <div className="text-slate-400">Loading…</div>}
      {albumsQuery.data && albumsQuery.data.length === 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
          No albums yet.
        </div>
      )}
      <ul className="space-y-2">
        {albumsQuery.data?.map((a) => (
          <AlbumCard key={a.id} album={a} editable={editable} />
        ))}
      </ul>

      <Style />
    </div>
  );
}

function AlbumCard({ album, editable }: { album: PhotoAlbum; editable: boolean }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.deletePhotoAlbum(album.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['photo-albums'] }),
  });
  const { playersWithPhoto, totalPlayers, freePhotos } = album.counts;
  const pct = totalPlayers > 0 ? (playersWithPhoto / totalPlayers) * 100 : 0;
  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <Link
            to={`/albums/${album.id}`}
            className="text-base font-medium hover:text-indigo-300"
          >
            {album.title}
          </Link>
          {album.description && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-400">
              {album.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span>Created {album.createdAt.slice(0, 10)}</span>
            <span
              className={
                album.status === 'open'
                  ? 'rounded bg-emerald-700/50 px-2 py-0.5 text-emerald-100'
                  : 'rounded bg-slate-700 px-2 py-0.5 text-slate-300'
              }
            >
              {album.status}
            </span>
            <span
              className="tabular-nums"
              title="Members with a photo in this album"
            >
              {playersWithPhoto} / {totalPlayers} members
            </span>
            <span
              className="tabular-nums text-slate-400"
              title="Free (non-member) photos"
            >
              +{freePhotos} free
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-slate-800">
            <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {editable && (
          <button
            className="text-slate-500 hover:text-red-400"
            title="Delete album"
            onClick={() => {
              if (
                confirm(
                  `Delete album "${album.title}"? All photos in this album will be removed.`,
                )
              )
                remove.mutate();
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </li>
  );
}

function CreateAlbumDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.createPhotoAlbum({
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['photo-albums'] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to create'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!title.trim()) return;
        create.mutate();
      }}
      className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4"
    >
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Title
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          required
          className="input"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Description (optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="input"
        />
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}
